import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { Subject } from 'rxjs';
import { UserSettingsDoc } from './user-settings.service';
import { applySystemZoom } from '../_util/system-zoom.util';

export interface AppState {
  preset: string;
  primary: string;
  surface: string | undefined | null;
  darkMode: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  _appState: AppState = {
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
    darkMode: false,
  };

  appState = signal<AppState>(this._appState);

  transitionComplete: WritableSignal<boolean> = signal<boolean>(false);

  private initialized = false;
  private appStateUpdate = new Subject<AppState>();

  appStateUpdate$ = this.appStateUpdate.asObservable();

  constructor() {
    effect(() => {
      const appState = this.appState();
      if (appState) {
        this.onAppStateUpdate();
      }
    });

    effect(() => {
      const state = this.appState();

      if (!this.initialized || !state) {
        this.initialized = true;
        return;
      }

      this.handleDarkModeTransition(state);
    });
  }

applyUserSettings(settings: UserSettingsDoc): void {
if (settings.primaryColor) {
  document.documentElement.style.setProperty('--primary-color', settings.primaryColor);
  this.appState.update(state => ({
    ...state,
    primary: settings.primaryColor!   // 👈 assert it's a string
  }));
}

  if (settings.editorZoomFactor !== undefined) {
    document.documentElement.style.setProperty('--fontScaleFactor', `${settings.editorZoomFactor}%`);
    document.documentElement.style.setProperty('--fontScaleRatio', `${settings.editorZoomFactor / 100}`);
    this.appState.update(state => ({
      ...state,
      editorZoomFactor: settings.editorZoomFactor
    }));
  }
  if (settings.systemZoomFactor !== undefined) {
    applySystemZoom(settings.systemZoomFactor);
    this.appState.update(state => ({
      ...state,
      systemZoomFactor: settings.systemZoomFactor
    }));
  }

  if (settings.theme) {
    this.appState.update(state => ({ ...state, theme: settings.theme }));
  }
}

  private handleDarkModeTransition(config: AppState): void {
    if ((document as any).startViewTransition) {
      this.startViewTransition(config);
    } else {
      this.toggleDarkMode(config);
      this.onTransitionEnd();
    }
  }

  private startViewTransition(config: AppState): void {
    const transition = (document as any).startViewTransition(() => {
      this.toggleDarkMode(config);
    });

    transition.ready
      .then(() => {
        this.onTransitionEnd();
      })
      .catch(() => {});
  }

  toggleDarkMode(appState?: AppState): void {
    const _appState = appState || this.appState();
    if (_appState.darkMode) {
      document.documentElement.classList.add('p-dark');
    } else {
      document.documentElement.classList.remove('p-dark');
    }
  }

  private onTransitionEnd() {
    this.transitionComplete.set(true);
    setTimeout(() => {
      this.transitionComplete.set(false);
    });
  }

  onAppStateUpdate() {
    this._appState = { ...this.appState() };
    this.appStateUpdate.next(this.appState());
    this.toggleDarkMode();
  }
}
